import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import paymentNotificationService from '../../../services/PaymentNotificationService';
import { toast } from 'sonner';

const ReminderCampaignList = ({ language }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [sendingReminder, setSendingReminder] = useState({});

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      fetchOverduePayments();
    }
  }, [currentUserId]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setCurrentUserId(user?.id);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
      toast.error('Erreur de connexion');
    }
  };

  const fetchOverduePayments = async () => {
    try {
      setLoading(true);

      // Récupérer toutes les factures en retard
      const { data: overdueInvoices, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          client_name,
          total_amount,
          amount_paid,
          balance_due,
          due_date,
          status,
          client_id,
          discount_amount,
          subtotal,
          countries (
            currency_code
          )
        `)
        .eq('client_id', currentUserId)
        .lt('due_date', new Date().toISOString().split('T')[0])
        .neq('status', 'paid')
        .neq('status', 'cancelled')
        .order('due_date', { ascending: true });

      if (invoicesError) throw invoicesError;

      // Récupérer les case_phase_invoices en retard
      const { data: cases } = await supabase
        .from('cases')
        .select('id')
        .eq('user_profile_id', currentUserId);

      const caseIds = cases?.map(c => c.id) || [];

      let phaseInvoices = [];
      if (caseIds.length > 0) {
        const { data: phaseData } = await supabase
          .from('case_phase_invoices')
          .select(`
            id,
            phase_number,
            phase_name,
            phase_price,
            billing_date,
            status,
            case_id,
            invoice:invoices (
              id,
              invoice_number,
              client_name,
              total_amount,
              due_date,
              status,
              countries (
                currency_code
              )
            )
          `)
          .in('case_id', caseIds)
          .eq('status', 'invoiced')
          .not('invoice_id', 'is', null);

        phaseInvoices = phaseData || [];
      }

      // Récupérer les rappels existants
      const allInvoiceIds = [
        ...overdueInvoices.map(inv => inv.id),
        ...phaseInvoices.filter(p => p.invoice).map(p => p.invoice.id)
      ];

      const { data: existingReminders } = await supabase
        .from('payment_reminders')
        .select('*')
        .in('invoice_id', allInvoiceIds)
        .order('created_at', { ascending: false });

      // Créer les campagnes
      const campaignData = await createCampaignsFromData(
        overdueInvoices,
        phaseInvoices,
        existingReminders
      );

      setCampaigns(campaignData);
    } catch (error) {
      console.error('Erreur lors du chargement des paiements en retard:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const createCampaignsFromData = async (invoices, phaseInvoices, reminders) => {
    const today = new Date();
    const currentMonth = today.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

    // Grouper par mois
    const activePayments = [];
    const completedPayments = [];

    // Traiter les factures directes
    for (const invoice of invoices || []) {
      const daysOverdue = Math.floor(
        (today - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24)
      );

      const relatedReminders = reminders?.filter(r => r.invoice_id === invoice.id) || [];
      const lastReminder = relatedReminders[0];

      const escalationLevel = relatedReminders.length > 0 
        ? Math.min(relatedReminders.length, 3) 
        : 0;

      const priority = daysOverdue > 14 ? 'high' : daysOverdue > 7 ? 'medium' : 'low';

      const payment = {
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        clientName: invoice.client_name,
        phaseName: 'Facture directe',
        amount: parseFloat(invoice.balance_due || invoice.total_amount),
        dueDate: invoice.due_date,
        daysOverdue: daysOverdue,
        escalationLevel: escalationLevel,
        lastReminderSent: lastReminder?.sent_date || null,
        nextAction: calculateNextAction(daysOverdue, escalationLevel),
        priority: priority,
        currencyCode: invoice.countries?.currency_code || 'EUR',
        status: invoice.status
      };

      if (invoice.status === 'paid') {
        completedPayments.push(payment);
      } else {
        activePayments.push(payment);
      }
    }

    // Traiter les factures de phases
    for (const phaseInv of phaseInvoices || []) {
      if (!phaseInv.invoice) continue;

      const invoice = phaseInv.invoice;
      const daysOverdue = Math.floor(
        (today - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24)
      );

      if (daysOverdue <= 0) continue;

      const relatedReminders = reminders?.filter(r => r.invoice_id === invoice.id) || [];
      const lastReminder = relatedReminders[0];

      const escalationLevel = relatedReminders.length > 0 
        ? Math.min(relatedReminders.length, 3) 
        : 0;

      const priority = daysOverdue > 14 ? 'high' : daysOverdue > 7 ? 'medium' : 'low';

      const payment = {
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        clientName: invoice.client_name,
        phaseName: phaseInv.phase_name,
        amount: parseFloat(invoice.total_amount),
        dueDate: invoice.due_date,
        daysOverdue: daysOverdue,
        escalationLevel: escalationLevel,
        lastReminderSent: lastReminder?.sent_date || null,
        nextAction: calculateNextAction(daysOverdue, escalationLevel),
        priority: priority,
        currencyCode: invoice.countries?.currency_code || 'EUR',
        status: invoice.status
      };

      if (invoice.status === 'paid') {
        completedPayments.push(payment);
      } else {
        activePayments.push(payment);
      }
    }

    const campaigns = [];

    // Campagne active
    if (activePayments.length > 0) {
      const totalAmount = activePayments.reduce((sum, p) => sum + p.amount, 0);
      const maxEscalation = Math.max(...activePayments.map(p => p.escalationLevel));
      const nextAction = activePayments
        .map(p => p.nextAction)
        .filter(d => d)
        .sort()[0];

      campaigns.push({
        id: 'active-' + today.getMonth(),
        campaignName: `Rappels ${currentMonth}`,
        status: 'active',
        totalOverdue: activePayments.length,
        totalAmount: totalAmount,
        escalationLevel: maxEscalation,
        nextAction: nextAction,
        payments: activePayments.sort((a, b) => b.daysOverdue - a.daysOverdue)
      });
    }

    // Campagne complétée (paiements payés ce mois)
    if (completedPayments.length > 0) {
      const totalAmount = completedPayments.reduce((sum, p) => sum + p.amount, 0);

      campaigns.push({
        id: 'completed-' + today.getMonth(),
        campaignName: 'Paiements récupérés',
        status: 'completed',
        totalOverdue: completedPayments.length,
        totalAmount: totalAmount,
        escalationLevel: 0,
        nextAction: null,
        payments: completedPayments
      });
    }

    return campaigns;
  };

  const calculateNextAction = (daysOverdue, escalationLevel) => {
    const today = new Date();
    let daysToAdd = 3;

    if (escalationLevel === 0) {
      daysToAdd = 3; // Premier rappel dans 3 jours
    } else if (escalationLevel === 1) {
      daysToAdd = 4; // Deuxième rappel dans 4 jours
    } else if (escalationLevel === 2) {
      daysToAdd = 7; // Dernier rappel dans 7 jours
    }

    const nextDate = new Date(today);
    nextDate.setDate(nextDate.getDate() + daysToAdd);
    return nextDate.toISOString().split('T')[0];
  };

  const handleSendReminder = async (payment) => {
    if (!currentUserId) {
      toast.error('Utilisateur non connecté');
      return;
    }

    try {
      setSendingReminder(prev => ({ ...prev, [payment.id]: true }));

      // Récupérer les paramètres de notification
      const settings = await paymentNotificationService.getNotificationSettings(currentUserId);

      // Créer le rappel
      const reminderData = {
        userId: currentUserId,
        invoiceId: payment.id,
        reminderType: payment.escalationLevel >= 2 ? 'escalation' : 'overdue',
        scheduledDate: new Date().toISOString().split('T')[0],
        amount: payment.amount,
        invoiceNumber: payment.invoiceNumber,
        dueDate: payment.dueDate,
        daysOverdue: payment.daysOverdue,
        messageText: paymentNotificationService.formatMessage(
          settings.overdueTemplate,
          {
            amount: formatAmount(payment.amount, payment.currencyCode),
            invoice_number: payment.invoiceNumber,
            days_overdue: payment.daysOverdue,
            client_name: payment.clientName
          }
        )
      };

      const reminder = await paymentNotificationService.createPaymentReminder(reminderData);

      // Marquer comme envoyé
      if (settings.notificationChannels?.email) {
        await paymentNotificationService.markReminderAsSent(reminder.id, 'email');
      }

      if (settings.notificationChannels?.dashboard) {
        await paymentNotificationService.createDashboardNotification({
          userId: currentUserId,
          title: 'Rappel de paiement envoyé',
          message: `Rappel envoyé pour la facture ${payment.invoiceNumber}`,
          type: 'info',
          invoiceId: payment.id,
          priority: payment.priority === 'high' ? 8 : 5
        });

        await paymentNotificationService.markReminderAsSent(reminder.id, 'dashboard');
      }

      // Ajouter à l'historique
      await paymentNotificationService.addNotificationHistory({
        userId: currentUserId,
        reminderId: reminder.id,
        invoiceId: payment.id,
        notificationType: 'reminder',
        channel: settings.notificationChannels?.email ? 'email' : 'dashboard',
        subject: `Rappel - ${payment.invoiceNumber}`,
        message: reminderData.messageText,
        status: 'sent'
      });

      toast.success('Rappel envoyé avec succès');

      // Recharger les données
      await fetchOverduePayments();
    } catch (error) {
      console.error('Erreur lors de l\'envoi du rappel:', error);
      toast.error('Erreur lors de l\'envoi du rappel');
    } finally {
      setSendingReminder(prev => ({ ...prev, [payment.id]: false }));
    }
  };

  const handlePauseCampaign = (campaignId) => {
    toast.info('Fonctionnalité à venir');
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      high: { 
        bgColor: 'bg-error/10', 
        borderColor: 'border-error/30', 
        textColor: 'text-error', 
        icon: 'AlertCircle',
        label: 'Critique'
      },
      medium: { 
        bgColor: 'bg-warning/10', 
        borderColor: 'border-warning/30', 
        textColor: 'text-warning', 
        icon: 'AlertTriangle',
        label: 'Moyen'
      },
      low: { 
        bgColor: 'bg-primary/10', 
        borderColor: 'border-primary/30', 
        textColor: 'text-primary', 
        icon: 'Info',
        label: 'Faible'
      }
    };
    return configs?.[priority] || configs?.low;
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { label: 'Actif', className: 'bg-success/10 text-success', icon: 'Play' },
      paused: { label: 'En pause', className: 'bg-warning/10 text-warning', icon: 'Pause' },
      completed: { label: 'Terminé', className: 'bg-muted text-muted-foreground', icon: 'CheckCircle' }
    };
    return configs?.[status] || configs?.active;
  };

  const formatAmount = (value, currencyCode = 'EUR') => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode
      }).format(value);
    } catch {
      return `${value} ${currencyCode}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12">
        <div className="flex items-center justify-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
          <p className="text-sm text-muted-foreground">Chargement des campagnes...</p>
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Aucun paiement en retard
        </h3>
        <p className="text-muted-foreground">
          Toutes vos factures sont à jour !
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {campaigns.map((campaign) => {
        const statusConfig = getStatusBadge(campaign.status);
        const isExpanded = expandedCampaign === campaign.id;
        const currencyCode = campaign.payments[0]?.currencyCode || 'EUR';

        return (
          <div key={campaign.id} className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Campaign Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{campaign.campaignName}</h3>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.className}`}>
                      <Icon name={statusConfig.icon} size={14} />
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Paiements en retard</p>
                      <p className="text-lg font-bold text-foreground">{campaign.totalOverdue}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Montant total</p>
                      <p className="text-lg font-bold text-foreground">
                        {formatAmount(campaign.totalAmount, currencyCode)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Niveau d'escalade</p>
                      <p className="text-lg font-bold text-foreground">{campaign.escalationLevel}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Prochaine action</p>
                      <p className="text-lg font-bold text-foreground">{formatDate(campaign.nextAction)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {campaign.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePauseCampaign(campaign.id)}
                    >
                      <Icon name="Pause" size={16} className="mr-2" />
                      Pause
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
                  >
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Expanded Payment List */}
            {isExpanded && campaign.payments.length > 0 && (
              <div className="p-6 bg-muted/30">
                <h4 className="text-sm font-semibold text-foreground mb-4">
                  Détails des paiements
                </h4>
                <div className="space-y-3">
                  {campaign.payments.map((payment) => {
                    const priorityConfig = getPriorityConfig(payment.priority);
                    const isSending = sendingReminder[payment.id];

                    return (
                      <div
                        key={payment.id}
                        className={`${priorityConfig.bgColor} border ${priorityConfig.borderColor} rounded-lg p-4`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <Icon name={priorityConfig.icon} size={20} className={priorityConfig.textColor} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-sm font-semibold text-foreground">{payment.invoiceNumber}</h5>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${priorityConfig.bgColor} ${priorityConfig.textColor} font-medium`}>
                                  {priorityConfig.label}
                                </span>
                              </div>
                              <p className="text-sm text-foreground mb-1">{payment.clientName}</p>
                              <p className="text-xs text-muted-foreground">{payment.phaseName}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span>Échéance: {formatDate(payment.dueDate)}</span>
                                {payment.lastReminderSent && (
                                  <span>Dernier rappel: {formatDate(payment.lastReminderSent)}</span>
                                )}
                                <span>Prochaine action: {formatDate(payment.nextAction)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-foreground mb-1">
                              {formatAmount(payment.amount, payment.currencyCode)}
                            </p>
                            <p className={`text-xs font-medium ${priorityConfig.textColor}`}>
                              Retard: {payment.daysOverdue} jours
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Escalade {payment.escalationLevel}
                            </p>
                            <Button
                              variant="outline"
                              size="xs"
                              className="mt-2"
                              onClick={() => handleSendReminder(payment)}
                              disabled={isSending}
                              loading={isSending}
                            >
                              <Icon name="Send" size={12} className="mr-1" />
                              Envoyer
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReminderCampaignList;